import { atomWithStorage } from 'jotai/utils'
import { GERMAN_UI_FLAG_KEY } from '@/modules/i18n/uiLanguageVisibility'

/** Persisted flag that temporarily re-enables German in the UI language picker. */
export const germanUiEnabledAtom = atomWithStorage<boolean>(
  GERMAN_UI_FLAG_KEY,
  false,
  undefined,
  { getOnInit: true }
)

/**
 * Visibility of the hidden Konami debug panel.
 * - `open`: full floating panel
 * - `minimized`: collapsed to a tiny re-openable bubble
 * - `closed`: fully hidden, only restorable via the Konami code
 */
export type DebugPanelState = 'open' | 'minimized' | 'closed'

export const debugPanelStateAtom = atomWithStorage<DebugPanelState>(
  'debug:panelState',
  'closed',
  undefined,
  { getOnInit: true }
)

const DEBUG_DEV_KEY = 'debugDev'
const DEBUG_DEV_ON = 'ken'

/**
 * Live read of the legacy `debugDev` flag. Reads localStorage on every call so
 * imperative callers (e.g. `withDebugDelay`) reflect the toggle immediately,
 * without a page reload. React consumers should use `debugDevEnabledAtom` so
 * they re-render when it flips.
 */
export function isDebugDevEnabled(): boolean {
  try {
    return window.localStorage.getItem(DEBUG_DEV_KEY) === DEBUG_DEV_ON
  } catch {
    return false
  }
}

/**
 * Reactive toggle for the legacy `debugDev` flag, stored as the raw string
 * "ken" (its established format) rather than JSON. Writing it keeps
 * `isDebugDevEnabled()` and all atom consumers in sync, live.
 */
export const debugDevEnabledAtom = atomWithStorage<boolean>(
  DEBUG_DEV_KEY,
  false,
  {
    getItem: (key, initial) => {
      try {
        return window.localStorage.getItem(key) === DEBUG_DEV_ON
      } catch {
        return initial
      }
    },
    setItem: (key, value) => {
      try {
        if (value) window.localStorage.setItem(key, DEBUG_DEV_ON)
        else window.localStorage.removeItem(key)
      } catch {
        /* localStorage blocked */
      }
    },
    removeItem: (key) => {
      try {
        window.localStorage.removeItem(key)
      } catch {
        /* localStorage blocked */
      }
    },
  },
  { getOnInit: true }
)

/** When debugDev is "ken", ensures the given promise takes at least minMs. Use to make loading steps visible for debugging. */
export function withDebugDelay<T>(label: string, minMs: number, fn: () => Promise<T>): Promise<T> {
  if (!isDebugDevEnabled()) {
    return fn()
  }
  return Promise.all([fn(), new Promise<void>(r => setTimeout(r, minMs))]).then(([result]) => result as T)
}

const DEBUG_ACCESS_GROUPS_KEY = 'debug:accessGroups'

/**
 * Admin-only override sent with GET /available-challenges so admins can browse
 * the challenges page as a user with these access groups. Persisted; applies
 * whether the debug panel is open, minimized or closed. The backend honors it
 * only for admins.
 */
export const debugAccessGroupsAtom = atomWithStorage<string[]>(
  DEBUG_ACCESS_GROUPS_KEY,
  [],
  undefined,
  { getOnInit: true }
)

/** Live read of the persisted override for code outside the React tree
 *  (e.g. the login-time challenges prefetch). */
export function getDebugAccessGroups(): string[] {
  try {
    const raw = window.localStorage.getItem(DEBUG_ACCESS_GROUPS_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((group): group is string => typeof group === 'string')
      : []
  } catch {
    return []
  }
}
